import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { QrCode, Plus, Trash, ScanSmiley, X, Printer, Package, Warning, Wrench, Camera, ImageSquare } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

/**
 * Fetches the equipment photo as a blob URL (authenticated GET). Returns the
 * object URL + a revoker. Falls back to null on 404 / error.
 */
function useEquipmentPhoto(equipmentId, photoStamp) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!equipmentId || !photoStamp) { setUrl(null); return undefined; }
    let alive = true;
    let objectUrl = null;
    api.get(`/installed-equipment/${equipmentId}/photo`, { responseType: "blob" })
      .then((r) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
      })
      .catch(() => alive && setUrl(null));
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [equipmentId, photoStamp]);
  return url;
}

const CATEGORIES = ["Chaudière", "Tableau électrique", "Disjoncteur", "VMC", "Climatisation", "Interphone", "Alarme", "Autre"];

export default function InstalledEquipmentCard({ chantier }) {
  const [items, setItems] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState(null); // full record with sav_history

  const load = async () => {
    try {
      const r = await api.get(`/installed-equipment?chantier_id=${chantier.id}`);
      setItems(r.data || []);
    } catch (err) { setItems([]); }
  };
  useEffect(() => { load(); }, [chantier?.id]);

  const openByQr = async (qr) => {
    try {
      const r = await api.get(`/installed-equipment/by-qr/${encodeURIComponent(qr)}`);
      setViewing(r.data);
    } catch (err) {
      toast.error(formatApiError(err) || "QR inconnu");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce matériel du chantier ?")) return;
    try {
      await api.delete(`/installed-equipment/${id}`);
      toast.success("Supprimé");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 space-y-3" data-testid="installed-equipment-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#005CFF]/15 border border-[#005CFF]/30 flex items-center justify-center">
            <Package size={17} weight="duotone" className="text-[#7CB2FF]" />
          </div>
          <div>
            <div className="font-semibold text-sm">Matériel installé chez le client</div>
            <div className="text-[11px] text-white/50">Scanne un QR ou enregistre du matériel pour le retrouver au prochain SAV.</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setScanning(true)}
            className="h-9 px-3 rounded-lg bg-[#005CFF] hover:bg-[#0047CC] text-xs font-semibold inline-flex items-center gap-1.5"
            data-testid="installed-equipment-scan"
          >
            <ScanSmiley size={13} weight="bold" /> Scanner
          </button>
          <button
            onClick={() => setAdding(true)}
            className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-xs inline-flex items-center gap-1.5"
            data-testid="installed-equipment-add"
          >
            <Plus size={12} weight="bold" /> Ajouter
          </button>
        </div>
      </div>

      {items === null && <div className="text-xs text-white/40">Chargement…</div>}
      {items && items.length === 0 && (
        <div className="text-xs text-white/40 italic text-center py-3">Aucun matériel enregistré sur ce chantier.</div>
      )}
      {items && items.length > 0 && (
        <div className="space-y-1.5">
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => openByQr(it.qr_code)}
              className="w-full flex items-center gap-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 px-2.5 py-2 text-sm text-left"
              data-testid={`installed-eq-row-${it.id}`}
            >
              <QrCode size={16} className="text-white/40 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">
                  {it.name}
                  {it.category && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-white/40">{it.category}</span>}
                </div>
                <div className="text-[10px] text-white/40 truncate">
                  {[it.brand, it.model, it.serial_number && `SN ${it.serial_number}`].filter(Boolean).join(" · ") || it.qr_code}
                </div>
              </div>
              <div className="text-[10px] text-white/50 shrink-0 tabular-nums">{it.installed_at || ""}</div>
              <button
                onClick={(e) => { e.stopPropagation(); remove(it.id); }}
                className="h-7 w-7 rounded-md hover:bg-red-500/20 hover:text-red-400 text-white/40 flex items-center justify-center shrink-0"
                title="Supprimer"
              >
                <Trash size={11} />
              </button>
            </button>
          ))}
        </div>
      )}

      {scanning && <QrScanDialog onClose={() => setScanning(false)} onScanned={(qr) => { setScanning(false); openByQr(qr); }} />}
      {adding && <AddDialog chantier={chantier} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {viewing && <DetailDialog item={viewing} onClose={() => setViewing(null)} onReload={() => openByQr(viewing.qr_code)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// QR scanner — uses html5-qrcode (works iOS Safari + Android Chrome)
// ---------------------------------------------------------------------------
function QrScanDialog({ onClose, onScanned }) {
  const scannerRef = useRef(null);
  const containerId = "installed-qr-scanner-region";

  useEffect(() => {
    let stopped = false;
    const scanner = new Html5Qrcode(containerId, { verbose: false });
    scannerRef.current = scanner;
    scanner.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: { width: 250, height: 250 } },
      (decoded) => {
        if (stopped) return;
        stopped = true;
        try { scanner.stop().catch(() => { /* ignore */ }); } catch (_) { /* ignore */ }
        onScanned(decoded);
      },
      () => { /* silent decode failures */ },
    ).catch((err) => {
      toast.error(`Caméra indisponible : ${err?.message || err}`);
      onClose();
    });
    return () => {
      stopped = true;
      try { scanner.stop().then(() => scanner.clear()).catch(() => { /* ignore */ }); } catch (_) { /* ignore */ }
    };
  }, [onScanned, onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="qr-scan-dialog">
      <div className="w-full max-w-md rounded-2xl bg-[#0A0A0A] border border-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="font-semibold text-sm">Scanner un QR matériel</div>
          <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center">
            <X size={14} />
          </button>
        </div>
        <div id={containerId} className="w-full aspect-square bg-black" />
        <div className="px-4 py-2 text-[11px] text-white/50 text-center">Centre le QR dans le cadre — la reconnaissance est automatique.</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add manual dialog
// ---------------------------------------------------------------------------
function AddDialog({ chantier, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", category: "", brand: "", model: "", serial_number: "", location_note: "", warranty_until: "",
  });
  const [saving, setSaving] = useState(false);
  const patch = (u) => setForm((f) => ({ ...f, ...u }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom obligatoire"); return; }
    setSaving(true);
    try {
      const r = await api.post("/installed-equipment", { ...form, chantier_id: chantier.id });
      toast.success(`Enregistré · QR ${r.data.qr_code}`);
      // Open print label immediately so tech can print the sticker
      window.open(`/print/qr/${r.data.qr_code}`, "_blank", "noopener");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" data-testid="installed-eq-add-dialog">
      <div className="w-full max-w-md rounded-2xl bg-[#0A0A0A] border border-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">Matériel installé</div>
            <div className="font-semibold">Nouveau matériel</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center">
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <Field label="Nom *" value={form.name} onChange={(v) => patch({ name: v })} testid="eq-name" placeholder="Ex : Chaudière cuisine" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Catégorie</div>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => patch({ category: c })}
                  className={`h-7 px-2.5 rounded-md text-[11px] transition-colors ${form.category === c ? "bg-[#005CFF] text-white" : "bg-white/5 hover:bg-white/10 text-white/70"}`}
                >{c}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Marque" value={form.brand} onChange={(v) => patch({ brand: v })} testid="eq-brand" />
            <Field label="Modèle" value={form.model} onChange={(v) => patch({ model: v })} testid="eq-model" />
          </div>
          <Field label="Numéro de série" value={form.serial_number} onChange={(v) => patch({ serial_number: v })} testid="eq-serial" />
          <Field label="Emplacement" value={form.location_note} onChange={(v) => patch({ location_note: v })} testid="eq-location" placeholder="Ex : sous-sol côté rue" />
          <Field label="Garantie jusqu'au" value={form.warranty_until} onChange={(v) => patch({ warranty_until: v })} type="date" testid="eq-warranty" />
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-white/10 bg-white/[0.02]">
          <button onClick={onClose} className="h-9 px-3 rounded-lg text-sm bg-white/5 hover:bg-white/10">Annuler</button>
          <button
            onClick={save}
            disabled={saving}
            className="h-9 px-4 rounded-lg text-sm font-semibold bg-[#005CFF] hover:bg-[#0047CC] disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="eq-save"
          >
            <Printer size={12} weight="bold" /> {saving ? "…" : "Enregistrer & imprimer QR"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-9 rounded-lg bg-white/5 border border-white/10 px-3 text-sm focus:outline-none focus:border-[#005CFF]"
        data-testid={testid}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail dialog — full info + SAV history
// ---------------------------------------------------------------------------
function DetailDialog({ item, onClose, onReload }) {
  const { user } = useAuth();
  const canEdit = ["admin", "manager", "associate", "compta", "technician"].includes(user?.role);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: item.name || "", brand: item.brand || "", model: item.model || "",
    serial_number: item.serial_number || "", location_note: item.location_note || "",
    warranty_until: item.warranty_until || "", notes: item.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const photoUrl = useEquipmentPhoto(item.id, item.photo_uploaded_at);

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Image trop volumineuse (max 15 Mo)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/installed-equipment/${item.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo enregistrée");
      onReload?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const removePhoto = async () => {
    if (!window.confirm("Supprimer la photo ?")) return;
    try {
      await api.delete(`/installed-equipment/${item.id}/photo`);
      toast.success("Photo supprimée");
      onReload?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/installed-equipment/${item.id}`, form);
      toast.success("Modifié");
      setEditing(false);
      onReload?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" data-testid="installed-eq-detail-dialog">
      <div className="w-full max-w-md rounded-2xl bg-[#0A0A0A] border border-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <QrCode size={18} className="text-[#7CB2FF]" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">{item.qr_code}</div>
              <div className="font-semibold truncate">{item.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <a
              href={`/print/qr/${item.qr_code}`}
              target="_blank"
              rel="noopener noreferrer"
              className="h-8 px-2 rounded-lg bg-white/5 hover:bg-white/10 text-[11px] inline-flex items-center gap-1"
            >
              <Printer size={11} /> Étiquette
            </a>
            <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center">
              <X size={14} />
            </button>
          </div>
        </div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {/* Photo section */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 flex items-center justify-between">
              <span className="flex items-center gap-1.5"><ImageSquare size={11} /> Photo</span>
              {photoUrl && canEdit && (
                <button onClick={removePhoto} className="text-white/40 hover:text-red-400 text-[10px] uppercase tracking-widest" data-testid="eq-photo-remove">
                  Supprimer
                </button>
              )}
            </div>
            {photoUrl ? (
              <a href={photoUrl} target="_blank" rel="noopener noreferrer" className="block rounded-lg overflow-hidden border border-white/10 hover:border-white/30 transition-colors">
                <img src={photoUrl} alt={item.name} className="w-full max-h-64 object-contain bg-black" data-testid="eq-photo-img" />
              </a>
            ) : (
              <div className="rounded-lg border border-dashed border-white/15 bg-white/[0.02] p-4 text-center text-xs text-white/40">
                Aucune photo — utile pour le SAV à distance et vérif marque/modèle.
              </div>
            )}
            {canEdit && (
              <div className="flex items-center gap-2 mt-2">
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="eq-photo-camera-input" />
                <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="eq-photo-file-input" />
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 h-9 px-3 rounded-lg bg-[#005CFF] hover:bg-[#0047CC] text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
                  data-testid="eq-photo-camera-btn"
                >
                  <Camera size={13} weight="bold" /> Prendre en photo
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                  data-testid="eq-photo-file-btn"
                >
                  <ImageSquare size={13} /> Galerie
                </button>
              </div>
            )}
          </div>

          {!editing ? (
            <div className="space-y-1.5 text-sm">
              {item.category && <Row label="Catégorie" value={item.category} />}
              {item.brand && <Row label="Marque" value={item.brand} />}
              {item.model && <Row label="Modèle" value={item.model} />}
              {item.serial_number && <Row label="N° série" value={item.serial_number} />}
              {item.location_note && <Row label="Emplacement" value={item.location_note} />}
              {item.installed_at && <Row label="Installé le" value={item.installed_at} />}
              {item.installed_by_name && <Row label="Par" value={item.installed_by_name} />}
              {item.warranty_until && <Row label="Garantie" value={item.warranty_until} highlight />}
              {item.notes && <Row label="Notes" value={item.notes} />}
              {canEdit && (
                <button
                  onClick={() => setEditing(true)}
                  className="mt-2 h-8 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-xs inline-flex items-center gap-1.5"
                  data-testid="eq-detail-edit"
                >
                  Modifier
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Field label="Nom" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <div className="grid grid-cols-2 gap-2">
                <Field label="Marque" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} />
                <Field label="Modèle" value={form.model} onChange={(v) => setForm({ ...form, model: v })} />
              </div>
              <Field label="N° série" value={form.serial_number} onChange={(v) => setForm({ ...form, serial_number: v })} />
              <Field label="Emplacement" value={form.location_note} onChange={(v) => setForm({ ...form, location_note: v })} />
              <Field label="Garantie jusqu'au" type="date" value={form.warranty_until} onChange={(v) => setForm({ ...form, warranty_until: v })} />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Notes</div>
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#005CFF]" />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => setEditing(false)} className="h-8 px-3 rounded-lg text-xs bg-white/5">Annuler</button>
                <button onClick={save} disabled={saving} className="h-8 px-3 rounded-lg text-xs font-semibold bg-[#005CFF] hover:bg-[#0047CC] disabled:opacity-40">
                  {saving ? "…" : "Enregistrer"}
                </button>
              </div>
            </div>
          )}

          {/* SAV history */}
          {(item.sav_history || []).length > 0 && (
            <div className="pt-2 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 flex items-center gap-1.5"><Wrench size={11} /> Historique SAV ({item.sav_history.length})</div>
              <div className="space-y-1">
                {item.sav_history.map((t) => (
                  <div key={t.id} className="flex items-center justify-between text-xs rounded-md bg-white/[0.03] px-2 py-1.5">
                    <div className="flex-1 min-w-0 truncate">{t.title}</div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {t.priority === "critical" && <Warning size={11} className="text-red-400" />}
                      <span className="text-[9px] uppercase tracking-widest text-white/40">{t.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex items-baseline gap-2">
      <div className="text-[10px] uppercase tracking-widest text-white/40 w-24 shrink-0">{label}</div>
      <div className={`text-sm ${highlight ? "text-emerald-400 font-semibold" : "text-white/80"}`}>{value}</div>
    </div>
  );
}
